import { zPaginate } from "@be/z/z-page";
import type { Project } from "@prisma/client-generated";
import { type ZodType, z } from "zod";
import { zBimObjectJson } from "./z-bim-object";
import { zCamera } from "./z-camera";
import { zLayout } from "./z-layout";
import { zUnderlay } from "./z-underlay";

const mapType = z.enum([
	"none",
	"map2D",
	"map2DOsmBuildings",
	"map2DBasemapDe",
	"map3D",
]);
export type MapType = z.infer<typeof mapType>;

/** Project */
export const zProject = z.object({
	id: z.string().uuid(),
	name: z
		.string()
		.min(1, "Project name is required")
		.max(255, "Project name must be less than 255 characters"),
	createdAt: z.date(),
	updatedAt: z.date(),
	camera: zCamera,
	countryCode: z
		.string()
		.min(2, "Country code must be 2 characters")
		.max(2, "Country code must be 2 characters")
		.or(z.literal("")),
	city: z.string().max(255, "City must be less than 255 characters"),
	mapType,
	defaultLayoutId: z.string().uuid(),
	defaultLayout: zLayout,
	layouts: z.array(zLayout),
}) satisfies ZodType<Project>;

export const zPostProjectReq = z.object({
	project: zProject.pick({
		name: true,
		countryCode: true,
		city: true,
	}),
	bimObjects: z.array(zBimObjectJson),
	pipeSettingsOverrides: z
		.object({
			wingSetbackFactor: z.number().optional(),
		})
		.optional(),
});

export const zPostProjectRes = zProject.pick({ id: true, name: true }).extend({
	layout: zLayout.pick({
		id: true,
		name: true,
	}),
});

export const zPatchProjectReq = zProject.pick({ name: true });

export const zPatchProjectRes = zProject.pick({ id: true, name: true });

export const zGetProjectsItemRes = zProject.pick({
	id: true,
	name: true,
	defaultLayoutId: true,
	createdAt: true,
	updatedAt: true,
});

export const zGetProjectsRes = zPaginate(zGetProjectsItemRes);

export const zGetProjectReq = z.object({ projectId: z.string().uuid() });

export const zGetProjectRes = zProject
	.pick({
		id: true,
		name: true,
		camera: true,
		countryCode: true,
		mapType: true,
	})
	.extend({ underlays: z.array(zUnderlay) });
